import { useSyncExternalStore } from 'react';

import { decodeCustomRange, decodeFilterValue, DATE_RANGE_CUSTOM } from '../registry/filters';

/**
 * What the screen the user is looking at is currently filtered by, published for consumers that
 * cannot reach the screen's own filter context.
 *
 * The Orbit panel is mounted in the app shell as a SIBLING of the routed tree
 * (`containers/App/pages/index.tsx`), while each screen's filter provider wraps that screen alone —
 * `InsightFiltersProvider` over Customer Insights, `Customer360QueryProvider` over Customer 360 — so
 * a `useContext` read from Orbit resolves to the inert default and never to the live selections.
 * Every screen that has filters therefore publishes its signature here and Orbit subscribes to it,
 * which is also what keeps Orbit from importing a second screen's filter internals. PLA-8844.
 */
export interface ScreenFilterChip {
  /** Stable, locale-independent id. The chip's `data-testid` is built from it. */
  id: string;
  /** The category's name, in the filter bar's own copy. */
  label: string;
  /** The selected option, in the filter bar's own copy. */
  value: string;
}

/**
 * One card the reader has on screen right now, published by the screen's own card tree as the page
 * scrolls (`useCardInView`). A card belongs to the screen's context because it is what the screen is
 * ANSWERING about: the same filters over Customer Behaviour Trends and over Current & Predicted
 * Customer Value are two different questions (PLA-8844, user 2026-09-14).
 */
export interface ScreenCardInView {
  /** The card's `registry/cards.ts` key. Locale-independent, so it is what carries the identity. */
  key: string;
  /** The card's own heading, exactly as the screen prints it. */
  title: string;
  /**
   * The first catalogue id the card's tiles name, or `null` for a card the registry maps to none.
   * `null` travels as no `metric_id` at all: FR §4.3 admits only an id that exists in the deployed
   * `metrics.yml` catalogue, so an absent field is the only honest alternative to a real one.
   */
  metricId: string | null;
}

/** The selected period as Orbit sends it: a preset id, or a custom window in YYYY-MM-DD (PLA-9791). */
export type ScreenDateRange =
  | { preset: string }
  | { preset: typeof DATE_RANGE_CUSTOM; start_date: string; end_date: string };

export interface ScreenFilterSignature {
  /**
   * Identity of the filter state, for change detection. Built from the raw option ids, so it is
   * independent of the UI locale: a language switch must not read as a context change. The card in
   * view is NOT part of it - it is `card` below, and it is compared separately.
   */
  key: string;
  /** Every filter the context section draws, in the order the screen's own filter bar lists them. */
  chips: ScreenFilterChip[];
  /**
   * Every card the reader currently has on screen, in reading order. Three or four cards are visible
   * at once on both Customer Insights and Customer 360, and naming one of them was naming the wrong
   * one as often as the right one (user, 2026-09-22). Empty until the screen's card tree has
   * reported a set.
   */
  cards?: ScreenCardInView[];
  /**
   * The topmost visible card, i.e. `cards[0]`. Kept as its own field because it is what the wire and
   * the change comparison reduce to, and what every consumer written before the set existed reads.
   */
  card?: ScreenCardInView | null;
  /**
   * The structured values behind the chips, for the wire (PLA-9791): every non-date filter off its
   * default, keyed by filter id with its raw selection, and the selected period. Built from the same
   * selections as the chips, so what is drawn and what is sent cannot drift apart.
   */
  filters?: Record<string, string>;
  dateRange?: ScreenDateRange;
}

/**
 * The wire half of a signature from a screen's raw selections. `dateRange` is the one category
 * always present; every other category is sent only when it is off its default, as it is drawn.
 */
export const screenWireFiltersOf = (
  selections: Record<string, string>,
  defaults: Record<string, string>
): Pick<ScreenFilterSignature, 'filters' | 'dateRange'> => {
  const filters: Record<string, string> = {};

  Object.keys(defaults).forEach(id => {
    if (id !== 'dateRange' && selections[id] !== undefined && selections[id] !== defaults[id]) {
      filters[id] = selections[id];
    }
  });

  const period = selections.dateRange ?? defaults.dateRange;
  const custom = decodeCustomRange(period);
  const dateRange: ScreenDateRange = custom
    ? { preset: DATE_RANGE_CUSTOM, start_date: custom[0], end_date: custom[1] }
    : { preset: decodeFilterValue(period).optionId };

  return { filters, dateRange };
};

type Listener = () => void;

/*
  The two halves have separate publishers — the screen's filter provider and its card tree — so they
  are held apart and joined into the snapshot below. Routing both through one setter would let
  whichever published last erase the other's half.
*/
let filterPart: ScreenFilterSignature | null = null;
let cardsPart: ScreenCardInView[] | null = null;
let signature: ScreenFilterSignature | null = null;
const listeners = new Set<Listener>();

export const getScreenFilterSignature = (): ScreenFilterSignature | null => signature;

export const subscribeScreenFilterSignature = (listener: Listener): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

/** Separator of the joined card keys. A registry card key never contains one. */
export const CARD_KEY_SEPARATOR = '|';

/**
 * Identity of a visible set: its card keys in reading order. What tells a real change of what the
 * reader has on screen from a re-confirmation of the same cards.
 */
export const cardSetKey = (cards: ScreenCardInView[]): string =>
  cards.map(card => card.key).join(CARD_KEY_SEPARATOR);

/** The drawn text of a signature — what a re-publish has to compare beyond the locale-free `key`. */
const textOf = (next: ScreenFilterSignature | null): string =>
  next === null
    ? ''
    : [
        ...(next.cards ?? []).map(card => `${card.key}${card.title}`),
        ...next.chips.map(chip => `${chip.label}${chip.value}`)
      ].join('|');

/**
 * Rebuilds the published snapshot from whichever halves exist and wakes every subscriber. `null` is
 * published only once BOTH halves are retracted, which is what leaving the screen does: a stale
 * period or a stale card would otherwise keep describing a screen the user is no longer on.
 *
 * A rebuild is dropped rather than published only when BOTH the identity and the drawn text are
 * unchanged, so a re-render that produced an equal value cannot wake every subscriber while a
 * language switch — same `key`, different words — still redraws the chips.
 */
const republish = (): void => {
  const next: ScreenFilterSignature | null =
    filterPart === null && cardsPart === null
      ? null
      : {
          // The card is deliberately NOT folded into `key`: the filters and the card are two
          // independent context changes, each with its own baseline in `OrbitProvider`, and joining
          // them would make a card arriving one frame after a page load read as a filter change.
          key: filterPart?.key ?? '',
          chips: filterPart?.chips ?? [],
          ...(filterPart?.filters === undefined ? {} : { filters: filterPart.filters }),
          ...(filterPart?.dateRange === undefined ? {} : { dateRange: filterPart.dateRange }),
          cards: cardsPart ?? [],
          card: cardsPart === null ? null : cardsPart[0] ?? null
        };

  if (
    next === null
      ? signature === null
      : signature?.key === next.key && textOf(signature) === textOf(next)
  ) {
    return;
  }

  signature = next;
  listeners.forEach(listener => listener());
};

export const publishScreenFilterSignature = (next: ScreenFilterSignature | null): void => {
  filterPart = next;
  republish();
};

/**
 * The card half: every card on screen, in reading order. Retracting it leaves the filters published,
 * since the user is still on the screen. An empty array is not a retraction — it is a scroll
 * position with no card in it — so `null` is the only retraction.
 */
export const publishScreenCardsInView = (next: ScreenCardInView[] | null): void => {
  cardsPart = next;
  republish();
};

/** The one-card form, for a caller that has a single card rather than a set. */
export const publishScreenCardInView = (next: ScreenCardInView | null): void =>
  publishScreenCardsInView(next === null ? null : [next]);

export const useScreenFilterSignature = (): ScreenFilterSignature | null =>
  useSyncExternalStore(
    subscribeScreenFilterSignature,
    getScreenFilterSignature,
    getScreenFilterSignature
  );

/**
 * How long the run of card titles may get before the compare row clamps it. The row is one line of a
 * 447px card with a label beside it, and a screenful of four cards can name 120 characters between
 * them; the full text stays reachable through the row's `title` attribute.
 */
export const CARDS_SUMMARY_MAX = 80;

/** The visible cards as one line — "Card A · Card B · Card C" — for the conflict card's rows. */
export const screenCardsSummary = (cards: ScreenCardInView[]): string => {
  const joined = cards.map(card => card.title).join(' · ');

  return joined.length <= CARDS_SUMMARY_MAX
    ? joined
    : `${joined.slice(0, CARDS_SUMMARY_MAX).trimEnd()}…`;
};

/**
 * The one-line form the conflict card's compare row uses: the cards on screen and the selected
 * values alone, since the row already names the screen and has no room to repeat every category
 * name.
 */
export const screenFilterSummary = (next: ScreenFilterSignature | null): string => {
  if (next === null) {
    return '';
  }

  const cards = screenCardsSummary(next.cards ?? []);

  return [...(cards === '' ? [] : [cards]), ...next.chips.map(chip => chip.value)].join(' · ');
};

export type Translate = (key: string, values?: Record<string, unknown>) => string;
